import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import {
  Modal,
  Portal,
  Text,
  TextInput,
  Button,
  Surface,
  IconButton,
  HelperText,
  Divider,
  List,
  Menu,
} from 'react-native-paper';
import { theme, spacing } from '../constants/theme';
import { ReviewCategory } from '../types';
import { isDetoxTestControlsEnabled } from '../config/environments';

interface OnDemandItemModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSave: (itemData: OnDemandItemData) => void;
  categories?: ReviewCategory[];
  loading?: boolean;
}

export interface OnDemandItemData {
  name: string;
  description?: string;
  categoryId?: number | null;
}

export const OnDemandItemModal: React.FC<OnDemandItemModalProps> = ({
  visible,
  onDismiss,
  onSave,
  categories = [],
  loading = false,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [categoryMenuVisible, setCategoryMenuVisible] = useState(false);
  const [errors, setErrors] = useState<{ name?: string }>({});

  // Reset form when modal opens
  useEffect(() => {
    if (visible) {
      setName('');
      setDescription('');
      setSelectedCategoryId(null);
      setErrors({});
    }
  }, [visible]);

  const handleSave = () => {
    // Validate
    const newErrors: { name?: string } = {};
    if (!name.trim()) {
      newErrors.name = 'Item name is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Save
    onSave({
      name: name.trim(),
      description: description.trim() || undefined,
      categoryId: selectedCategoryId,
    });
  };

  const getSelectedCategoryName = () => {
    if (!selectedCategoryId) return 'No Category';
    const category = findCategoryById(categories, selectedCategoryId);
    return category?.name || 'No Category';
  };

  const findCategoryById = (cats: ReviewCategory[], id: number): ReviewCategory | null => {
    for (const cat of cats) {
      if (cat.id === id) return cat;
      if (cat.subcategories) {
        const found = findCategoryById(cat.subcategories, id);
        if (found) return found;
      }
    }
    return null;
  };

  const renderCategoryOptions = (cats: ReviewCategory[], level = 0): React.ReactNode[] => {
    const options: React.ReactNode[] = [];
    
    cats.forEach(cat => {
      const indent = '  '.repeat(level);
      options.push(
        <Menu.Item
          key={cat.id}
          onPress={() => {
            setSelectedCategoryId(cat.id);
            setCategoryMenuVisible(false);
          }}
          title={`${indent}${cat.name}`}
          leadingIcon={level > 0 ? 'subdirectory-arrow-right' : undefined}
        />
      );
      
      if (cat.subcategories && cat.subcategories.length > 0) {
        options.push(...renderCategoryOptions(cat.subcategories, level + 1));
      }
    });
    
    return options;
  };

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={styles.modalContent}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.keyboardAvoid}
        >
          <Surface testID="on-demand-item-modal" style={styles.surface}>
            <View style={styles.header}>
              <Text variant="headlineSmall" style={styles.title}>
                Add Custom Item
              </Text>
              {isDetoxTestControlsEnabled && (
                <Button
                  testID="on-demand-item-create-button-top"
                  mode="contained"
                  compact
                  onPress={handleSave}
                  loading={loading}
                  disabled={loading}
                  style={styles.detoxCreateButton}
                >
                  Create
                </Button>
              )}
              <IconButton
                icon="close"
                size={24}
                onPress={onDismiss}
                style={styles.closeButton}
              />
            </View>

            <Divider />

            <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
              <View style={styles.form}>
                <TextInput
                  testID="on-demand-item-name-input"
                  label="Item Name *"
                  value={name}
                  onChangeText={(text) => {
                    setName(text);
                    if (errors.name) {
                      setErrors({ ...errors, name: undefined });
                    }
                  }}
                  mode="outlined"
                  error={!!errors.name}
                  disabled={loading}
                  style={styles.input}
                />
                <HelperText type="error" visible={!!errors.name}>
                  {errors.name}
                </HelperText>

                <TextInput
                  testID="on-demand-item-description-input"
                  label="Description (Optional)"
                  value={description}
                  onChangeText={setDescription}
                  mode="outlined"
                  multiline
                  numberOfLines={3}
                  disabled={loading}
                  style={styles.input}
                />

                <View style={styles.categorySection}>
                  <Text variant="labelLarge" style={styles.categoryLabel}>
                    Category (Optional)
                  </Text>
                  <Menu
                    visible={categoryMenuVisible}
                    onDismiss={() => setCategoryMenuVisible(false)}
                    anchor={
                      <Button
                        mode="outlined"
                        onPress={() => setCategoryMenuVisible(true)}
                        icon="chevron-down"
                        contentStyle={styles.categoryButton}
                      >
                        {getSelectedCategoryName()}
                      </Button>
                    }
                    style={styles.categoryMenu}
                  >
                    <ScrollView style={{ maxHeight: 300 }}>
                      <Menu.Item
                        onPress={() => {
                          setSelectedCategoryId(null);
                          setCategoryMenuVisible(false);
                        }}
                        title="No Category"
                        leadingIcon="close-circle-outline"
                      />
                      <Divider />
                      {renderCategoryOptions(categories)}
                    </ScrollView>
                  </Menu>
                </View>

                <View style={styles.infoBox}>
                  <Text variant="bodySmall" style={styles.infoText}>
                    This item will be added to your current review only. 
                    It will have default questions for room/location and general notes, 
                    which you can customize after creation.
                  </Text>
                </View>
              </View>
            </ScrollView>

            <Divider />

            <View style={styles.actions}>
              <Button
                testID="on-demand-item-cancel-button"
                mode="text"
                onPress={onDismiss}
                disabled={loading}
                style={styles.actionButton}
              >
                Cancel
              </Button>
              <Button
                testID="on-demand-item-create-button"
                mode="contained"
                onPress={handleSave}
                loading={loading}
                disabled={loading}
                style={styles.actionButton}
              >
                Create Item
              </Button>
            </View>
          </Surface>
        </KeyboardAvoidingView>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modalContent: {
    backgroundColor: 'white',
    margin: 20,
    borderRadius: 8,
    maxHeight: '80%',
    minHeight: 400,
  },
  keyboardAvoid: {
    flex: 1,
    minHeight: 400,
  },
  surface: {
    borderRadius: 8,
    backgroundColor: theme.colors.surface,
    flex: 1,
    minHeight: 400,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  title: {
    flex: 1,
  },
  closeButton: {
    margin: 0,
  },
  detoxCreateButton: {
    marginRight: spacing.xs,
  },
  content: {
    flexGrow: 0,
    flexShrink: 1,
  },
  form: {
    padding: spacing.md,
  },
  input: {
    marginBottom: spacing.sm,
  },
  categorySection: {
    marginTop: spacing.sm,
    marginBottom: spacing.md,
  },
  categoryLabel: {
    marginBottom: spacing.xs,
  },
  categoryButton: {
    justifyContent: 'space-between',
  },
  categoryMenu: {
    marginTop: 40,
  },
  infoBox: {
    backgroundColor: theme.colors.secondaryContainer,
    padding: spacing.sm,
    borderRadius: 4,
    marginTop: spacing.sm,
  },
  infoText: {
    color: theme.colors.onSecondaryContainer,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: spacing.md,
    gap: spacing.sm,
  },
  actionButton: {
    minWidth: 100,
  },
});
